import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Label as LabelIcon,
  SwapVert as SwapVertIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { tagsAPI } from '../services/api';

function TagsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' ou 'desc'
  const [deleteDialog, setDeleteDialog] = useState({ open: false, tag: null });

  // Requête pour récupérer les tags
  const { data, isLoading, error } = useQuery(
    ['tags', page, search],
    () => tagsAPI.getAll({ page, limit: 12, search }).then(res => res.data),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000,
    }
  );

  // Mutation pour supprimer un tag
  const deleteTagMutation = useMutation(
    (id) => tagsAPI.delete(id),
    {
      onSuccess: () => {
        toast.success(t('tagsPage.deleteSuccess'));
        queryClient.invalidateQueries('tags');
        setDeleteDialog({ open: false, tag: null });
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          toast.error(t('tagsPage.deleteError'));
        } else {
          toast.error(t('tagsPage.deleteGenericError'));
        }
      }
    }
  );


  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleAdd = () => {
    navigate('/tags/ajouter');
  };

  const handleEdit = (tag) => {
    navigate(`/tags/${tag.id}/modifier`);
  };

  const handleDelete = (tag) => {
    setDeleteDialog({ open: true, tag });
  };

  const confirmDelete = () => {
    if (deleteDialog.tag) {
      deleteTagMutation.mutate(deleteDialog.tag.id);
    }
  };


  const handleTagClick = (tagId) => {
    navigate(`/tags/${tagId}`);
  };

  const handleSortToggle = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  // Trier les tags par nom
  const sortedTags = React.useMemo(() => {
    if (!data?.data) return [];
    const tags = [...data.data];
    tags.sort((a, b) => {
      const comparison = a.nom.localeCompare(b.nom, 'fr');
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return tags;
  }, [data?.data, sortOrder]);

  if (error) {
    return (
      <Alert severity="error">
        {t('tagsPage.loadingError', { message: error.message })}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Barre de recherche, bouton tri et bouton Ajouter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder={t('tagsPage.searchPlaceholder')}
          value={search}
          onChange={handleSearchChange}
          sx={{
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.default',
              borderRadius: 2,
            }
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          }}
        />
        <Button
          variant="outlined"
          startIcon={<SwapVertIcon />}
          onClick={handleSortToggle}
          sx={{
            height: '52px',
            borderRadius: 2,
            fontSize: { xs: '14px', sm: '14px' },
            whiteSpace: 'nowrap',
            backgroundColor: 'background.default',
            px: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            borderColor: 'rgba(0, 0, 0, 0.23)',
            color: 'text.primary',
            '& .MuiButton-startIcon': {
              mr: { xs: 0, sm: 0.75 }
            },
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
              backgroundColor: 'action.hover',
            }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {sortOrder === 'asc' ? t('tagsPage.sort.nameAsc') : t('tagsPage.sort.nameDesc')}
          </Box>
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{
            height: '52px',
            borderRadius: 2,
            fontSize: { xs: '14px', sm: '14px' },
            whiteSpace: 'nowrap',
            px: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            '& .MuiButton-startIcon': {
              mr: { xs: 0, sm: 0.75 }
            }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {t('tagsPage.add')}
          </Box>
        </Button>
      </Box>

      {/* Résultats */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {sortedTags.length === 0 ? (
            <Alert severity="info">
              {search ? t('tagsPage.noResults') : t('tagsPage.empty')}
            </Alert>
          ) : (
            <Card sx={{ borderRadius: 2 }}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Typography 
                  variant="h6" 
                  component="h2"
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '18px',
                    mb: 0.5,
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  }}
                  >
                    {t('tagsPage.title')}
                  </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  }}
                >
                  {data?.pagination?.total === 1 
                    ? t('tagsPage.count', { count: data?.pagination?.total || 0 })
                    : t('tagsPage.countPlural', { count: data?.pagination?.total || 0 })}
                </Typography>
              </Box>
              <Divider />
              <List sx={{ py: 0 }}>
                {sortedTags.map((tag, index) => {
                  const tagColor = tag.couleur || '#1976d2';
                  return (
                    <React.Fragment key={tag.id}>
                      <ListItem
                        button
                        onClick={() => handleTagClick(tag.id)}
                        sx={{
                          py: 2,
                          px: 3,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            backgroundColor: tagColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            flexShrink: 0,
                          }}
                        >
                          <LabelIcon sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 700,
                                  fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                                }}
                              >
                                {tag.nom}
                              </Typography>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: tagColor,
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            tag.description && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                                }}
                              >
                                {tag.description}
                              </Typography>
                            )
                          }
                        />
                        <Box 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/livres?tag_id=${tag.id}`);
                          }}
                          sx={{ 
                            textAlign: 'right', 
                            ml: 2,
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.15)',
                              transform: 'scale(1.05)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 400,
                              color: 'primary.main',
                              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                            }}
                          >
                            {tag.nombre_livres || 0}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'primary.main',
                              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                            }}
                          >
                            {tag.nombre_livres === 1 
                              ? t('tagsPage.bookCount', { count: tag.nombre_livres || 0 })
                              : t('tagsPage.bookCountPlural', { count: tag.nombre_livres || 0 })}
                          </Typography>
                        </Box>
                      </ListItem>
                      {index < sortedTags.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  );
                })}
              </List>

              {/* Pagination */}
              {data?.pagination?.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, pt: 2 }}>
                  <Pagination
                    count={data.pagination.totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </Card>
          )}
        </>
      )}

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, tag: null })}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle>{t('tagsPage.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('tagsPage.deleteConfirm', { name: deleteDialog.tag?.nom })}
          </Typography>
          {deleteDialog.tag?.nombre_livres > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('tagsPage.deleteWarning', { count: deleteDialog.tag.nombre_livres })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, tag: null })}>
            {t('tagsPage.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            disabled={deleteTagMutation.isLoading || deleteDialog.tag?.nombre_livres > 0}
          >
            {deleteTagMutation.isLoading ? t('tagsPage.deleting') : t('tagsPage.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TagsPage;
